import { AxiosResponse } from 'axios';

import { normalizeBaseGist, normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { prepareFormData } from 'utils/form';
import { IAllPageFilters, IBaseGist, INullable, IPageType, TObject } from 'utils/types';

import axiosInstance from '../base';

import {
  normalizeReconciliationFile,
  normalizeReconciliationFolder,
  normalizeReconciliationFolderMapping,
  normalizeReconciliationFolderMappingValues,
  normalizeReconciliationFolderPayload,
  normalizeReconciliationResults,
  normalizeStartReconciliationPayload,
} from './normalizers';
import {
  IBulkResolveTransactionPayload,
  IGetReconciliationFilePayload,
  IMappingParametersFormPayload,
  IMappingValuesFormPayload,
  IReconciliationFile,
  IReconciliationFileForm,
  IReconciliationFolder,
  IReconciliationFolderData,
  IReconciliationFolderForm,
  IReconciliationFolderMapping,
  IReconciliationFolderMappingValues,
  IReconciliationFolderPayload,
  IReconciliationResults,
  IResolveTransactionPayload,
  IStartReconciliationPayload,
} from './types';

const reconciliationApi = {
  async getAllReconcileFolders(
    filters: IAllPageFilters,
  ): Promise<IPageType<IReconciliationFolder>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('recon/folders', {
      params: normalizedFilter,
    });

    return normalizePageData<IReconciliationFolderData, IReconciliationFolder>(
      response.data,
      normalizeReconciliationFolder,
      JSON.stringify(filters?.search),
    );
  },

  async getReconcileFolder(id: string): Promise<IReconciliationFolder> {
    const { data } = await axiosInstance.get(`recon/folders/${id}`);
    return normalizeReconciliationFolder(data);
  },

  async createReconcileFolder(
    payload: IReconciliationFolderForm,
  ): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(
      normalizeReconciliationFolderPayload(payload),
    );
    const response = await axiosInstance.post('recon/folders', normalizedPayload);
    return response;
  },

  async updateReconcileFolder({
    id,
    ...data
  }: IReconciliationFolderPayload): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(
      normalizeReconciliationFolderPayload(data.payload),
    );
    const response = await axiosInstance.put(`recon/folders/${id}`, normalizedPayload);
    return response;
  },

  async deleteReconcileFolder(id: number | string): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`recon/folders/${id}`);
    return response;
  },

  async createReconcileFile({
    id,
    ...others
  }: IReconciliationFileForm): Promise<IBaseGist> {
    const response = await axiosInstance.post(`recon/folders/${id}/files`, others.file);
    return normalizeBaseGist(response?.data);
  },

  async deleteReconcileFile({
    fileId,
    id,
  }: {
    fileId?: number | string;
    id?: number | string;
  }): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`recon/folders/${id}/files/${fileId}`);
    return response;
  },

  async getReconciliationResults(
    filters: IAllPageFilters,
    id?: string,
  ): Promise<IPageType<IReconciliationResults>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get(`recon/folders/${id}/results`, {
      params: normalizedFilter,
    });

    return normalizePageData<TObject, IReconciliationResults>(
      response.data,
      normalizeReconciliationResults,
      JSON.stringify(filters.search),
    );
  },

  async getReconcileFolderMapping(id: string): Promise<IReconciliationFolderMapping> {
    const { data } = await axiosInstance.get(`recon/folders/${id}/mapping`);
    return normalizeReconciliationFolderMapping(data);
  },

  async editMappingParameters({
    folderId,
    ...others
  }: IMappingParametersFormPayload): Promise<AxiosResponse> {
    const normalizedMappingParameters = prepareFormData(others);
    const result = await axiosInstance.post(
      `recon/folders/${folderId}/mapping`,
      normalizedMappingParameters,
    );

    return result;
  },

  async getReconcileFolderMappingValues(
    folderId: string,
    mappingId: INullable<number>,
  ): Promise<IReconciliationFolderMappingValues> {
    const { data } = await axiosInstance.get(
      `recon/folders/${folderId}/mapping/${mappingId}/fields-mapping`,
    );
    return normalizeReconciliationFolderMappingValues(data);
  },

  async editMappingValues({
    folderId,
    mappingId,
    ...others
  }: IMappingValuesFormPayload): Promise<AxiosResponse> {
    const normalizedMappingParameters = prepareFormData(others);
    const result = await axiosInstance.post(
      `recon/folders/${folderId}/mapping/${mappingId}/fields-mapping`,
      normalizedMappingParameters,
    );

    return result;
  },

  async startReconciliation({
    fileId,
    folderId,
    payload,
  }: IStartReconciliationPayload): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(
      normalizeStartReconciliationPayload(payload),
    );
    const response = await axiosInstance.post(
      `recon/folders/${folderId}/files/${fileId}`,
      normalizedPayload,
    );
    return response;
  },

  async getReconciliationFile({
    fileId,
    folderId,
  }: IGetReconciliationFilePayload): Promise<IReconciliationFile> {
    const { data } = await axiosInstance.get(`recon/folders/${folderId}/files/${fileId}`);
    return normalizeReconciliationFile(data);
  },

  async bulkResolveTransaction({
    fileId,
    filters,
    folderId,
    payload,
  }: IBulkResolveTransactionPayload): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);
    const { search } = normalizeSearchFiltersPayload(filters);

    return axiosInstance.post(
      `recon/folders/${folderId}/files/${fileId}/transactions/resolve/bulk`,
      normalizedPayload,
      { params: { search } },
    );
  },

  async resolveTransaction({
    fileId,
    folderId,
    payload,
  }: IResolveTransactionPayload): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);
    const response = await axiosInstance.post(
      `recon/folders/${folderId}/files/${fileId}/transactions/resolve`,
      normalizedPayload,
    );
    return response;
  },
};

export default reconciliationApi;
